import React from 'react';
import './ProductChildrenGrid.scss';

/**
 * ProductChildrenGrid Component
 * Displays children nodes in a grid layout (OBO style)
 * Used for category/subcategory pages
 * 
 * Matches OBO pattern:
 * - Image 2: Category page with 6 tiles in 3x2 grid
 * - Image 3: Subcategory page with 8 component cards in grid
 */
const ProductChildrenGrid = ({ 
  children = [], 
  productId,
  productSlug,
  currentPath = [], 
  onNodeClick,
  onDetailClick,
  viewMode = 'grid'
}) => {
  // Use same-origin on live so product images load
  const API_URL = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
    ? window.location.origin
    : (process.env.REACT_APP_API_URL || 'http://localhost:3005').replace(/\/api\/?$/, '').replace(/\/+$/, '');

  const resolveImageEntry = (image) => {
    if (!image) return { url: '', alt: '' };
    if (typeof image === 'string') {
      return { url: image, alt: '' };
    }
    return { url: image.url || image.path || '', alt: image.alt || '' };
  };

  // Helper to normalize path (remove double slashes)
  const normalizePath = (base, path) => {
    const baseUrl = (base || '').replace(/\/$/, '');
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${cleanPath}`;
  };

  // Helper function to generate slug from title
  const generateSlug = (title) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_-]+/g, '-') // Replace spaces, underscores, and multiple hyphens with single hyphen
      .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
  };

  const handleClick = (node) => {
    if (node.is_detail) {
      if (onDetailClick) {
        onDetailClick(node.id);
      } else {
        const path = currentPath.join(',');
        window.location.href = normalizePath(process.env.PUBLIC_URL, `/product-details/${productId}?nodeId=${node.id}&path=${path}`);
      }
    } else {
      if (onNodeClick) {
        onNodeClick(node.id);
      } else {
        // Use slug instead of ID, generate from title if missing (backward compatibility)
        const slug = node.slug || generateSlug(node.title) || `node-${node.id}`;
        const newPath = [...currentPath, slug];
        const slugForUrl = productSlug || productId; // Use product slug if available, fallback to ID
        window.location.href = normalizePath(process.env.PUBLIC_URL, `/product-nodes/${slugForUrl}/${newPath.join('/')}`);
      }
    }
  };

  if (children.length === 0) {
    return (
      <div className="product-children-empty">
        <p>No items found.</p>
      </div>
    );
  }

  if (viewMode === 'list') {
    return (
      <div className="product-children-list">
        {children.map((node) => {
          const metadata = node.metadata || {};
          // Support both metadata.images and direct images array
          const images = Array.isArray(metadata.images)
            ? metadata.images
            : (Array.isArray(node.images) ? node.images : []);
          const firstImageEntry = images.length > 0 ? resolveImageEntry(images[0]) : null;
          let firstImage = null;
          if (firstImageEntry?.url) {
            const imageUrl = firstImageEntry.url;
            if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
              firstImage = imageUrl;
            } else if (imageUrl.startsWith('/uploads/')) {
              firstImage = `${API_URL}${imageUrl}`;
            } else if (imageUrl) {
              // Assume it's a relative path without leading slash
              firstImage = `${API_URL}/uploads/product/${imageUrl}`;
            }
          }
          const firstAlt = firstImageEntry?.alt || node.title;

          return (
            <div 
              key={node.id} 
              className="product-children-list-item"
              onClick={() => handleClick(node)}
            >
              <div className="list-item-image">
                {firstImage ? (
                  <img 
                    src={firstImage} 
                    alt={firstAlt}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                    }}
                  />
                ) : (
                  <div className="list-item-placeholder">No Image</div>
                )}
              </div>
              <div className="list-item-content">
                <h3 className="list-item-title">{node.title}</h3>
                {node.item_no && (
                  <p className="list-item-meta">Item: {node.item_no}</p>
                )}
                {node.type && (
                  <p className="list-item-meta">Type: {node.type}</p>
                )}
              </div>
              <div className="list-item-arrow">→</div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="product-children-grid">
      {children.map((node) => {
        const metadata = node.metadata || {};
        // Support both metadata.images and direct images array
        // Images can be stored in metadata.images or node.images
        const images = Array.isArray(metadata.images)
          ? metadata.images
          : (Array.isArray(node.images) ? node.images : []);
        const firstImageEntry = images.length > 0 ? resolveImageEntry(images[0]) : null;
        let firstImage = null;
        if (firstImageEntry?.url) {
          const imageUrl = firstImageEntry.url;
          if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
            firstImage = imageUrl;
          } else if (imageUrl.startsWith('/uploads/')) {
            firstImage = `${API_URL}${imageUrl}`;
          } else if (imageUrl) {
            // Assume it's a relative path without leading slash
            firstImage = `${API_URL}/uploads/product/${imageUrl}`;
          }
        }
        const firstAlt = firstImageEntry?.alt || node.title;

        return (
          <div 
            key={node.id} 
            className="product-children-card"
            onClick={() => handleClick(node)}
          >
            <div className="children-card-image">
              {firstImage ? (
                <img 
                  src={firstImage} 
                  alt={firstAlt}
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
                  }}
                />
              ) : (
                <div className="children-card-placeholder">
                  <span>No Image</span>
                </div>
              )}
            </div>
            <div className="children-card-content">
              <h3 className="children-card-title">{node.title}</h3>
              {node.item_no && (
                <p className="children-card-meta">Item: {node.item_no}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProductChildrenGrid;
